import type { SearchIntent } from '@/types/search-intent'
import { RUN_TIMED_OUT_ERROR_CODE, type RunEntry, type SetupRun } from '@/types/setup-run'

export type CompetitorStatus = 'never-analysed' | 'ok' | 'failed'

export const COMPETITOR_ANALYSIS_ERROR_CODES = [
  'search_vendor_http_error',
  'search_vendor_envelope_error',
  'search_vendor_task_missing',
  'search_vendor_task_error',
  'search_vendor_not_configured',
  'measurement_deadline_exceeded',
  'competitor_measurement_failed_all',
  'search_snapshot_missing',
  'competitor_analysis_already_running',
  'brand_domain_unknown',
  'invalid_website_url',
  'competitor_analysis_enqueue_failed',
  'search_snapshot_enqueue_failed',
  'competitor_analysis_failed',
] as const

type CompetitorAnalysisErrorCode = (typeof COMPETITOR_ANALYSIS_ERROR_CODES)[number]

export interface CompetitorLastError {
  readonly code: CompetitorAnalysisErrorCode
  readonly at: string
}

/** A competitor the brand tracks, identified by the rival's own domain. */
export interface Competitor {
  readonly competitorId: string
  readonly name: string
  readonly domain: string
  readonly websiteUrl: string
  /** What the company does and for whom, from the discovery, or `null` for a company the founder named. */
  readonly reason: string | null
  readonly addedAt: string | null
  readonly status: CompetitorStatus
  readonly analyzedAt?: string
  readonly lastAttemptAt?: string
  readonly lastError?: CompetitorLastError
}

/** One entity's standing across the comparison set; the traffic and keyword counts are `null` when the vendor overview failed. */
export interface CompetitorReportEntity {
  readonly competitorId: string | null
  readonly domain: string
  readonly estimatedTraffic: number | null
  readonly rankingKeywordCount: number | null
  readonly shareOfSearch: number
  readonly aiOverviewCitationCount: number
}

export interface CompetitorOverlap {
  readonly competitorId: string
  readonly sharedKeywordCount: number
  readonly theirsOnlyKeywordCount: number
  readonly oursOnlyKeywordCount: number
}

export interface CompetitorPositioning {
  readonly competitorId: string
  readonly line: string
}

export interface CompetitorTopicStanding {
  readonly topicId: string
  readonly competitorId: string | null
  readonly keywordCount: number
  readonly averagePosition: number | null
}

export interface CompetitorWinnableKeyword {
  readonly keyword: string
  readonly volume: number | null
  readonly difficulty: number | null
  readonly intent: SearchIntent | null
  readonly bestRivalPosition: number | null
  readonly bestRivalUrl: string | null
  readonly competitorId: string | null
}

export interface CompetitorRivalPage {
  readonly competitorId: string
  readonly url: string
  readonly etv: number | null
  readonly count: number | null
}

export interface SearchMarket {
  readonly locationCode: number
  readonly languageCode: string
}

/** Cached competitor comparison (`competitorReport/current`): one pass over every tracked rival plus the brand's own domain. */
export interface CompetitorReport {
  readonly comparisonSetSize: number
  readonly entities: readonly CompetitorReportEntity[]
  readonly overlaps: readonly CompetitorOverlap[]
  readonly positioning: readonly CompetitorPositioning[]
  readonly topicStandings: readonly CompetitorTopicStanding[]
  readonly generatedAt: string
  readonly competitorsChangedAt?: string
  readonly gaps: number
  readonly wins: number
  readonly contested: number
  readonly winnable: readonly CompetitorWinnableKeyword[]
  readonly rivalPages: readonly CompetitorRivalPage[]
  readonly market?: SearchMarket
  readonly snapshotFetchedAt?: string
}

export const MAX_TRACKED_COMPETITORS = 4

export const COMPETITOR_DISCOVERY_STAGES = ['snapshot', 'similar', 'search', 'rank'] as const

export type CompetitorDiscoveryStage = (typeof COMPETITOR_DISCOVERY_STAGES)[number]

export const DISCOVERY_LIST_IDS = [
  'linkedin-similar',
  'category-search',
  'comparison-search',
  'search-rivals',
  'named-rivals',
] as const

export type DiscoveryListId = (typeof DISCOVERY_LIST_IDS)[number]

export interface DiscoveryListEntry extends RunEntry {
  readonly id: DiscoveryListId
  readonly found: number
}

export interface BrandCandidate {
  readonly name: string
  readonly domain: string
  readonly websiteUrl: string
}

export const COMPETITOR_CANDIDATE_GROUPS = ['direct', 'alternative', 'not-relevant'] as const

export type CompetitorCandidateGroup = (typeof COMPETITOR_CANDIDATE_GROUPS)[number]

export interface CompetitorSearchOverlap {
  readonly intersections: number
  readonly sharedTraffic: number | null
}

/** How much of one named company's search a candidate shares. */
export interface CompetitorNamedOverlap extends CompetitorSearchOverlap {
  /** The named company's bare domain. */
  readonly of: string
}

export interface CompetitorCandidate extends BrandCandidate {
  readonly lists: readonly DiscoveryListId[]
  readonly group: CompetitorCandidateGroup
  readonly reason: string | null
  readonly searchOverlap: CompetitorSearchOverlap | null
  readonly namedOverlap: readonly CompetitorNamedOverlap[]
}

export const MAX_PICKED_COMPETITORS = 4

export const COMPETITOR_DISCOVERY_ERROR_CODES = [
  'competitor_discovery_enqueue_failed',
  'competitor_discovery_failed',
  RUN_TIMED_OUT_ERROR_CODE,
] as const

export type CompetitorDiscoveryErrorCode = (typeof COMPETITOR_DISCOVERY_ERROR_CODES)[number]

export const COMPETITOR_DISCOVERY_RUN_STALE_AFTER_MS = 5 * 60 * 1000

export interface CompetitorCandidateRun extends SetupRun<
  CompetitorDiscoveryStage,
  DiscoveryListEntry
> {
  readonly errorCode?: CompetitorDiscoveryErrorCode
  readonly candidates: readonly CompetitorCandidate[]
  readonly startedByUserId: string
}

/** A company the founder names before the discovery. */
export interface KnownCompetitor {
  readonly name: string
  readonly websiteUrl: string
}

/** A picked company the confirm tracks; `reason` is `null` for a company the founder named. */
export interface CompetitorChoice extends KnownCompetitor {
  readonly reason: string | null
}
